import { parsePartNumber } from "./validateSupplyPartNumber";

/**
 * Validates a product's Part Number (product.assembly_part_number), such as
 * "1", "1-3" or "1+3". Returns an error message, or "" when valid.
 * Moved unchanged from AddSupplyUnitPage so other forms can share it.
 */
export function validateProductPartNumber(value: string): string {
  if (!value) return "Part Number is required";
  if (!/^\d+([+-]\d+)*$/.test(value)) {
    return "Part Number must contain only numbers separated by '+' or '-'";
  }
  const parts: string[] = parsePartNumber(value);
  if (new Set(parts).size !== parts.length) {
    return "Part Number must not contain duplicate values";
  }
  const nums = parts.map((n) => parseInt(n, 10));
  for (let i = 1; i < nums.length; i++) {
    if (nums[i] < nums[i - 1])
      return "Part Number values must be in ascending order (e.g 1-2 NOT 2-1)";
  }
  if (!parts.every((n) => Number(n) >= 1)) {
    return "Part Number values must be greater than 0";
  }
  return "";
}

/**
 * Smallest Part Number that covers every selected supply unit's part
 * number, e.g. ["1", "2", "4"] -> "1-2+4", or ["2"] -> "2".
 * Returns null when nothing usable is selected or a part number isn't numeric.
 */
export function derivePartNumber(
  units: ReadonlyArray<{ part_number?: string | null }>
): string | null {
  const nums = new Set<number>();
  for (const unit of units) {
    const partNumber = (unit.part_number ?? "").trim();
    if (!partNumber) continue;
    if (!/^\d+([+-]\d+)*$/.test(partNumber)) return null;
    for (const value of parsePartNumber(partNumber) as string[]) {
      nums.add(parseInt(value, 10));
    }
  }
  if (nums.size === 0) return null;

  const sorted = Array.from(nums).sort((a, b) => a - b);
  const groups: string[] = [];
  let start = sorted[0];
  let prev = sorted[0];
  for (const n of sorted.slice(1)) {
    if (n === prev + 1) {
      prev = n;
      continue;
    }
    groups.push(start === prev ? `${start}` : `${start}-${prev}`);
    start = n;
    prev = n;
  }
  groups.push(start === prev ? `${start}` : `${start}-${prev}`);
  return groups.join("+");
}
